import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { NavLink, Link } from "react-router-dom";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import clsx from "clsx";

import { appRoutes, externalRoutes } from "routes";
import { useUsers, useScreen, useGoogleOptimize, useRouter } from "hooks";
import UserPresenter from "presenters/UserPresenter";
import SearchLinksPresenter from "presenters/SearchLinksPresenter";
import SYBPromoImg from "assets/images/SYB-promo.png";
import SYBVdayPromoImg from "assets/images/SYB-promo-vday.png";
import SYBXmasPromoImg from "assets/images/SYB-promo-xmas.jpg";
import SYBFDayPromoImgA from "assets/images/SYB_FDay2022_Menu_Banner_A.jpg";
import SYBFDayPromoImgB from "assets/images/SYB_FDay2022_Menu_Banner_B.jpg";
import ResponsiveImage from "components/ResponsiveImage";
import logoVertical from "assets/images/common/logo-vertical.png";

import { NOT_AUTH_LINKS, AUTH_LINKS } from "./menuItems";
import useStyles from "./useStyles";

const SYB_PROMO_IMG = {
  sybpromo: SYBPromoImg,
  christmas: SYBXmasPromoImg,
  vday: SYBVdayPromoImg,
  fdaysideA: SYBFDayPromoImgA,
  fdaysideB: SYBFDayPromoImgB,
};

const getPromo = (str) => {
  let promo = "";
  switch (str) {
    case "vday":
      promo = "vday";
      break;
    case "christmas":
      promo = "christmas";
      break;
    case "fdaysideA":
      promo = "fdaysideA";
      break;
    case "fdaysideB":
      promo = "fdaysideB";
      break;
    case "true":
      promo = "sybpromo";
      break;
    default:
      break;
  }
  return promo ? { promo, img: SYB_PROMO_IMG[promo] } : null;
};

const SideMenu = (props) => {
  const classes = useStyles();
  const { onClose, searchLinks } = props;
  const { currentUser, signOut } = useUsers();
  const { isDesktop } = useScreen();
  const [showPromoNavBar, setShowPromoNavBar] = useState(null);

  const {
    testValues: { syb_promo: SYBPromo },
  } = useGoogleOptimize();
  const { location } = useRouter();

  useEffect(() => {
    setShowPromoNavBar(getPromo(SYBPromo));
  }, [SYBPromo]);

  const siteVersion = process.env.GIT_BRANCH.replace("hotfix/", "").replace("release/", "");

  const handleClick = () => onClose();

  const renderMenuItem = (link, key = 0) => {
    const { to: pathname, name, exact = false, button = false, activeLink = true } = link;
    const activeClassName = button ? classes.buttonLinkActive : classes.linkActive;
    const isLoginLink = name === "Log In";
    const toParams = { pathname };
    if (isLoginLink) toParams.state = { referrer: location.pathname };
    return (
      <NavLink
        id={`hamburger-${name.replace(/\s/g, "-").toLowerCase()}`}
        onClick={() => handleClick()}
        to={toParams}
        key={key}
        exact={exact}
        className={button ? classes.buttonLink : classes.link}
        activeClassName={activeLink ? activeClassName : ""}
      >
        <span>{name}</span>
      </NavLink>
    );
  };

  const renderSearchLink = (searchLink) => {
    const name = SearchLinksPresenter.name(searchLink);
    return (
      <NavLink
        id={`hamburger-${name.replace(/\s/g, "-").toLowerCase()}`}
        onClick={() => handleClick()}
        key={SearchLinksPresenter.id(searchLink)}
        className={classes.link}
        to={SearchLinksPresenter.url(searchLink)}
      >
        {SearchLinksPresenter.name(searchLink)}
      </NavLink>
    );
  };

  return (
    <div className={classes.root}>
      <IconButton
        id="hamburger-x"
        disableRipple
        color="primary"
        aria-label="menu"
        onClick={onClose}
        className={clsx(classes.closeButton, {
          [classes.closeBtnDesktopPosition]: isDesktop,
          [classes.closeBtnMobilePosition]: !isDesktop,
        })}
        size="large"
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>

      {!isDesktop && searchLinks && searchLinks.map((searchLink) => renderSearchLink(searchLink))}

      {!isDesktop && <div className={classes.delimiter} />}

      {UserPresenter.isRegistered(currentUser) && (
        <NavLink
          onClick={() => handleClick()}
          to={appRoutes.myEcardsPath()}
          className={classes.link}
          activeClassName={classes.linkActive}
        >
          <span>Ecards</span>
        </NavLink>
      )}

      {UserPresenter.isRegistered(currentUser)
        ? AUTH_LINKS.map((link, index) => renderMenuItem(link, index))
        : NOT_AUTH_LINKS.map((link, index) => renderMenuItem(link, index))}

      {UserPresenter.isRegistered(currentUser) && (
        <NavLink id="hamburger-logout" onClick={() => signOut()} to={appRoutes.rootPath()} className={classes.link}>
          <span>Logout</span>
        </NavLink>
      )}

      {UserPresenter.isRegistered(currentUser) && !UserPresenter.isPaid(currentUser) && (
        <Link
          id="hamburger-upgrade"
          onClick={() => handleClick()}
          to={appRoutes.paymentPath()}
          className={classes.buttonLink}
        >
          <span>Upgrade</span>
        </Link>
      )}

      <a
        className={classes.link}
        href={externalRoutes.helpPath()}
        onClick={() => handleClick()}
        target="_blank"
        rel="noreferrer"
        id="hamburger-help"
      >
        Help
      </a>

      {isDesktop && <div className={classes.delimiter} />}

      {showPromoNavBar ? (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a
          href={`https://books.jibjab.com/?utm_source=jjwebsite&utm_medium=navlink&utm_campaign=${showPromoNavBar.promo}`}
          target="_blank"
          className={classes.sybPromoLink}
          id="hamburger-syb-promo"
        >
          <ResponsiveImage
            className={classes.sybLogo}
            src={showPromoNavBar.img}
            alt="books jibjab"
            width={246.32}
            widthMobile={171}
          />
        </a>
      ) : (
        <NavLink to={appRoutes.rootPath()} className={classes.logoLink}>
          <img src={logoVertical} alt="JibJab" className={classes.logo} />
        </NavLink>
      )}

      <span className={classes.siteVersion}>{siteVersion}</span>
    </div>
  );
};

SideMenu.propTypes = {
  onClose: PropTypes.func.isRequired,
  searchLinks: PropTypes.arrayOf(PropTypes.shape()),
};

export default SideMenu;
